/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/bravada-column-bands.ts
 * FILE_ID: QB-AGGRID-BRAVADA-BANDS-001
 * PURPOSE: Column-band schema that mirrors Bravada's actual Excel
 *          "Scope & Pricing" master template — used when the
 *          organisation's `quote_builder_mode === 'waterproofing'`.
 *          Steel / custom modes continue to use BASE_COLUMN_BANDS.
 *
 * WHY: Bravada estimators paste from their existing `.xlsm` quote
 *      sheets every day. If the AG-Grid header row doesn't match the
 *      master-template Excel they have to stop and re-learn which
 *      column does what.
 *
 * EVIDENCE: Audited Q9525R9_Underslab_Membrane_Bulleen_Final.xlsm
 *   "Scope & Pricing" sheet (header rows 7-15). Excel column → field id
 *   provenance lives next to each numCol() call below + in
 *   bravada-pricing-bands.ts for the pricing-section bands.
 *
 * BAND LAYOUT (left → right, Excel order):
 *   1. Identity            — Item, Works Location
 *   2. Description         — Description (col F), Products / System (col M)
 *   3. Measure             — Area (col Q), Sub Total excl GST (col R)
 *   4. Total Chargeout     — Adjusted $/m² (col T), Calc $/m² (col U),
 *                            $ total (col V)
 *   5. Labour              — PMPD, Days, Hours, Cost, Charge Out
 *                            (cols W-AA)
 *   6. Products            — Unit cost + coverage (cols AB-AC) + 8
 *                            aggregate cols (cols AM-AT). See
 *                            bravada-pricing-bands.ts.
 *   7. Plant Rental        — Description, Units, Unit Cost, Total
 *                            Cost, Markup % / $, Charge Out
 *                            (cols AU-BA). See bravada-pricing-bands.ts.
 *   8. Subcontractor       — Same shape as Plant (cols BB-BH). See
 *                            bravada-pricing-bands.ts.
 *
 * KNOWN LIMITATION (tracked for follow-up MR):
 *   The "Unit cost" + "m², LM/unit" column pair in the PRODUCTS band
 *   maps temporarily to material_cost + material_chargeout because
 *   the backend stores LINE-TOTAL only. Future MR will split this
 *   into material_unit_cost + material_coverage_per_unit on the
 *   model, then route the Primer / Basecoat / Topcoat / Accessories
 *   sub-bands through QuoteLineItem.materials[].
 *
 * Plant/subcon columns are populated for line items where the legacy
 * dump captured them (the Q9525R9 main quote has subcon revenue on
 * 2 of 61 lines, plant zero — matching the source Excel). The columns
 * exist for all rows because Excel shows them too even when blank.
 *
 * RELATED:
 *   - QB-AGGRID-BRAVADA-PRICING-001 (bravada-pricing-bands.ts) —
 *     PRODUCTS aggregate cols + Plant + Subcon bands.
 *   - QB-AGGRID-BRAVADA-HELPERS-001 (bravada-band-helpers.ts) —
 *     textCol / numCol / WIDTH shared between both files.
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — consumer.
 *   - QB-AGGRID-FMT-001 (scope-ag-grid-formatters.ts) — formatters +
 *     computed-value resolution for the NEW column ids.
 *   - QB-SCOPESHEET-COLUMNS-001 (scope-sheet-columns.ts) — the steel
 *     BASE_COLUMN_BANDS that this schema sits beside.
 *   - QB-LIB-TRADEMODE-001 (lib/trade-mode.ts) — defines 'waterproofing'.
 */

import type { ColumnBand } from '../scope-sheet-grid/ScopeSheetGridTypes'
import { numCol, textCol, WIDTH } from './bravada-band-helpers'
import { BRAVADA_PRICING_BANDS } from './bravada-pricing-bands'
import { BRAVADA_OVERRIDES_BAND } from './bravada-overrides-band'

// ---------------------------------------------------------------------------
// IDENTITY band — Excel cols A-B
// ---------------------------------------------------------------------------

const IDENTITY_BAND: ColumnBand = {
  id: 'identity',
  label: 'Identity',
  color: 'slate',
  bgClass: 'bg-slate-50 dark:bg-slate-900/50',
  textClass: 'text-slate-600 dark:text-slate-400',
  columns: [
    // Excel col A "Item" — synthetic row-number computed at render
    // time from the row's position within its section. NEVER editable.
    { id: 'item_number', label: 'Item', width: WIDTH.itemNumber, align: 'center', computed: true },
    // Excel col B "Works Location".
    textCol({ id: 'location', label: 'Works Location', width: WIDTH.location }),
  ],
}

// ---------------------------------------------------------------------------
// DESCRIPTION band — Excel cols F + M
// ---------------------------------------------------------------------------

const DESCRIPTION_BAND: ColumnBand = {
  id: 'description',
  label: 'Description',
  color: 'indigo',
  bgClass: 'bg-indigo-50/50 dark:bg-indigo-950/30',
  textClass: 'text-indigo-600 dark:text-indigo-400',
  columns: [
    // Excel col F "Description". Flexes to fill viewport at scroll-0
    // (configured in scope-ag-grid-columns.ts).
    textCol({ id: 'description', label: 'Description', width: WIDTH.description }),
    // Excel col M "Products / System".
    textCol({
      id: 'product_system',
      label: 'Products / System',
      width: WIDTH.productSystem,
    }),
  ],
}

// ---------------------------------------------------------------------------
// MEASURE band — Excel cols Q + R
// ---------------------------------------------------------------------------

const MEASURE_BAND: ColumnBand = {
  id: 'measure',
  label: 'Measure',
  color: 'cyan',
  bgClass: 'bg-cyan-50/50 dark:bg-cyan-950/30',
  textClass: 'text-cyan-600 dark:text-cyan-400',
  columns: [
    // Excel col Q "Area (m2, LM etc)".
    numCol({ id: 'quantity', label: 'Area (m², LM etc)', width: WIDTH.area }),
    // Excel col R "Sub Total (excl GST)" — same data as `subtotal`
    // but Excel duplicates it here for the estimator's eye. We route
    // through the `subtotal_excl_gst` synthetic id so AG-Grid lets us
    // render the duplicate column; the formatter resolves it to the
    // same value as `subtotal` from computeLine.
    numCol({
      id: 'subtotal_excl_gst',
      label: 'Sub Total (excl GST)',
      width: WIDTH.subTotalExclGst,
      computed: true,
    }),
  ],
}

// ---------------------------------------------------------------------------
// TOTAL CHARGEOUT band — Excel cols T-V
// ---------------------------------------------------------------------------

const TOTAL_CHARGEOUT_BAND: ColumnBand = {
  id: 'total_chargeout',
  label: 'TOTAL CHARGEOUT (by Line Item)',
  color: 'yellow',
  bgClass: 'bg-yellow-50/50 dark:bg-yellow-950/30',
  textClass: 'text-yellow-700 dark:text-yellow-400',
  columns: [
    // Excel col T "ADJUSTED $/m², LM" — override input.
    numCol({
      id: 'adjusted_rate',
      label: 'ADJUSTED $/m², LM',
      width: WIDTH.rateAdjusted,
      override: true,
      editable: true,
    }),
    // Excel col U "CALCULATED $/m², LM" — engine output.
    numCol({
      id: 'rate_per_unit',
      label: 'CALC $/m², LM',
      width: WIDTH.rateCalculated,
      computed: true,
    }),
    // Excel col V "CALCULATED $ total" — engine output. Same value as
    // Sub Total (excl GST) at col R when the override matches the calc.
    numCol({
      id: 'subtotal',
      label: '$ total',
      width: WIDTH.totalDollars,
      computed: true,
    }),
  ],
}

// ---------------------------------------------------------------------------
// LABOUR band — Excel cols W-AA
// ---------------------------------------------------------------------------

const LABOUR_BAND: ColumnBand = {
  id: 'labour',
  label: 'LABOUR',
  color: 'blue',
  bgClass: 'bg-blue-50/50 dark:bg-blue-950/30',
  textClass: 'text-blue-600 dark:text-blue-400',
  columns: [
    // Excel col W "Standard Productivity PMPD".
    numCol({
      id: 'productivity_pmpd',
      label: 'Standard Productivity PMPD',
      width: WIDTH.pmpd,
    }),
    // Excel col X "Total Labour Man Days".
    numCol({
      id: 'labour_days',
      label: 'Total Labour Man Days',
      width: WIDTH.manDays,
      computed: true,
    }),
    // Excel col Y "Total Labour Man Hours".
    numCol({
      id: 'labour_hours',
      label: 'Total Labour Man Hours',
      width: WIDTH.manHours,
      computed: true,
    }),
    // Excel col Z "Total Labour Cost".
    numCol({
      id: 'labour_cost',
      label: 'Total Labour Cost',
      width: WIDTH.labourCost,
    }),
    // Excel col AA "Total Labour Chargeout".
    numCol({
      id: 'labour_chargeout',
      label: 'Total Labour Chargeout',
      width: WIDTH.labourChargeout,
    }),
  ],
}

// ---------------------------------------------------------------------------
// Assembled in Excel left-to-right order. Pricing bands (PRODUCTS,
// PLANT RENTAL, SUBCONTRACTOR) live in bravada-pricing-bands.ts so each
// file stays under the 300-line cap.
// ---------------------------------------------------------------------------

export const BRAVADA_COLUMN_BANDS: ColumnBand[] = [
  IDENTITY_BAND,
  DESCRIPTION_BAND,
  MEASURE_BAND,
  TOTAL_CHARGEOUT_BAND,
  LABOUR_BAND,
  ...BRAVADA_PRICING_BANDS,
  // MR H: per-line override band appears LAST so it doesn't crowd the
  // Excel-mirrored core layout. Estimators who need to set overrides
  // scroll right to reach it; everyone else gets the Excel-parity view
  // they're used to.
  BRAVADA_OVERRIDES_BAND,
]

/**
 * Stable id list of all column ids that exist in the Bravada bands.
 * Used at build time to compute the diff against BASE_COLUMN_BANDS so
 * any advanced opt-in ids (e.g. internal_notes) toggled on via the
 * Setup picker can be appended as extra bands in waterproofing mode
 * without duplicating columns that already exist in the Bravada layout.
 */
export const BRAVADA_BUILTIN_COLUMN_IDS: ReadonlySet<string> = new Set(
  BRAVADA_COLUMN_BANDS.flatMap((b) => b.columns.map((c) => c.id)),
)
